/**
 * PolicyEditor — the rich-text editor for DocumentDetail.tsx (the Security space's Document
 * Register, which is where Hub-linked policies/processes are actually edited). Built on
 * TipTap rather than react-quill/Quill: Quill's actual core version here is 1.3.7 (confirmed
 * by reading node_modules - react-quill's own "2.0.0" version number is unrelated to the
 * Quill core it bundles), which has no table support at all, and every Quill table plugin
 * either requires real Quill 2.x or is abandoned. TipTap's table extension is MIT-licensed,
 * actively maintained, and - critically for an app where a plain HTML string is the only
 * storage format - renders to plain `<table><tr><td>` with just colspan/rowspan/colwidth as
 * extra attributes, a small and fully-known sanitiser allowlist delta.
 *
 * Content in/out is always a plain HTML string via editor.getHTML() - never Quill's Delta
 * JSON, which the old ReactQuill single-editor produced on every edit regardless of how the
 * document was loaded (a real, separate bug: a Hub document, extracted as clean HTML, would
 * silently flip to storing `{"ops":[...]}` the moment someone edited and saved it here, and
 * RichTextView's HTML-detection would then render that JSON as literal visible text). Storing
 * HTML unconditionally removes that failure mode entirely.
 *
 * Extends the stock Paragraph node to round-trip a `class="ql-indent-N"` attribute - the
 * nested numbered/lettered/roman sub-list indentation the PDF-extraction pipeline produced
 * for the migrated Hub content. TipTap's schema drops any attribute a node doesn't explicitly
 * declare, so without this, opening one of those documents here and saving would silently
 * flatten every sub-list back to the left margin.
 *
 * Inline images (DocImage, in PolicyEditorImage.ts) follow the same never-trust-the-src
 * principle as the rest of this app's uploads: stored content only ever carries a stable
 * image id, resolved to a live URL client-side. See that file's doc-comment for the full
 * reasoning. Alignment and callout blocks (PolicyEditorExtensions.ts) follow the same
 * class-enum-not-style principle established by IndentParagraph.
 *
 * The toolbar is one bar, always visible, sticky to the top of the viewport as you scroll -
 * not a menu that follows the current selection. That was tried first (a TipTap BubbleMenu),
 * on the theory that a fixed bar is "far from the cursor" on a long document, but it read as
 * erratic in practice (jumping to wherever you'd last clicked, occasionally covering the very
 * text you were reading) - and a plain sticky bar was never actually broken by being fixed in
 * the first place, only by an overflow:hidden ancestor silently defeating position:sticky (see
 * policy-editor.css's .np-policy-editor-sticky-toolbar for the full explanation). Its contents
 * still adapt to context - plain text gets formatting controls, being inside a table adds row/
 * column controls, and a selected image swaps in alignment controls entirely - it's the
 * position that stopped following the selection, not the content.
 */
import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Paragraph from '@tiptap/extension-paragraph';
import { TableKit } from '@tiptap/extension-table/kit';
import { Placeholder } from '@tiptap/extensions';
import { DocImage, extractImageIds, registerResolvedImageUrls, registerBlobImageUrl } from './PolicyEditorImage';
import { DocPdf, extractPdfIds, registerResolvedPdfUrls, registerBlobPdfUrl } from './PolicyEditorPdf';
import { AlignAttribute, Callout, type AlignValue, type CalloutType } from './PolicyEditorExtensions';
import { documentsApi } from '../services/api';
import { useToast } from '../hooks/useToast';

const IndentParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('class'),
        renderHTML: (attributes: { class?: string | null }) =>
          attributes.class ? { class: attributes.class } : {},
      },
    };
  },
});

const HEADING_LEVELS = [1, 2, 3, 4, 5] as const;
const IMAGE_TYPES = ['image/png', 'image/jpeg'];
const PDF_TYPE = 'application/pdf';

interface PolicyEditorProps {
  value: string;
  onChange: (html: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: number;
  /** The document this content belongs to - required to upload a new inline image (it
   * becomes the S3/document-row owner), so image insertion is unavailable until the document
   * has been saved at least once and has a real id. */
  documentId?: string;
}

export const PolicyEditor: React.FC<PolicyEditorProps> = ({
  value, onChange, readOnly, placeholder, minHeight = 320, documentId,
}) => {
  const lastKnownValue = useRef(value);
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  // handleDrop/handlePaste are captured once, inside the useEditor() call below, before the
  // editor they need to act on exists yet - a ref sidesteps that chicken-and-egg problem
  // without recreating the editor (and losing undo history) every time documentId changes.
  const editorRef = useRef<Editor | null>(null);
  const documentIdRef = useRef(documentId);
  useEffect(() => { documentIdRef.current = documentId; }, [documentId]);

  const handleImageFile = async (targetEditor: Editor, file: File) => {
    const docId = documentIdRef.current;
    if (!docId) {
      toast.error('Save the document before adding images');
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    try {
      const uploaded = await documentsApi.upload('document-image', docId, file);
      registerBlobImageUrl(targetEditor, uploaded.documentId, blobUrl);
      targetEditor.chain().focus().insertContent({
        type: 'docImage',
        attrs: { docImageId: uploaded.documentId, alt: file.name },
      }).run();
    } catch (err) {
      URL.revokeObjectURL(blobUrl);
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to upload image: ${message}`);
    }
  };

  const handlePdfFile = async (targetEditor: Editor, file: File) => {
    const docId = documentIdRef.current;
    if (!docId) {
      toast.error('Save the document before adding PDFs');
      return;
    }
    const blobUrl = URL.createObjectURL(file);
    try {
      const uploaded = await documentsApi.upload('document-pdf', docId, file);
      registerBlobPdfUrl(targetEditor, uploaded.documentId, blobUrl);
      targetEditor.chain().focus().insertContent({
        type: 'docPdf',
        attrs: { docPdfId: uploaded.documentId, fileName: file.name },
      }).run();
    } catch (err) {
      URL.revokeObjectURL(blobUrl);
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to upload PDF: ${message}`);
    }
  };

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        paragraph: false,
        link: { openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } },
      }),
      IndentParagraph,
      TableKit.configure({ table: { resizable: true } }),
      Placeholder.configure({ placeholder: placeholder || '' }),
      DocImage,
      DocPdf,
      AlignAttribute,
      Callout,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      lastKnownValue.current = editor.getHTML();
      onChange(lastKnownValue.current);
    },
    editorProps: {
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        const imageFile = files.find((f) => IMAGE_TYPES.includes(f.type));
        const pdfFile = files.find((f) => f.type === PDF_TYPE);
        if (!editorRef.current || (!imageFile && !pdfFile)) return false;
        event.preventDefault();
        if (imageFile) handleImageFile(editorRef.current, imageFile);
        if (pdfFile) handlePdfFile(editorRef.current, pdfFile);
        return true;
      },
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        const imageFile = files.find((f) => IMAGE_TYPES.includes(f.type));
        const pdfFile = files.find((f) => f.type === PDF_TYPE);
        if (!editorRef.current || (!imageFile && !pdfFile)) return false;
        event.preventDefault();
        if (imageFile) handleImageFile(editorRef.current, imageFile);
        if (pdfFile) handlePdfFile(editorRef.current, pdfFile);
        return true;
      },
    },
  }, []);

  useEffect(() => { editorRef.current = editor; }, [editor]);

  // Controlled-value sync: only push external content in when it didn't originate from this
  // editor's own last onUpdate (comparing against the raw prop, not getHTML(), since TipTap's
  // serialised HTML can differ trivially from hand-written source - e.g. attribute ordering -
  // without the content actually having changed).
  useEffect(() => {
    if (!editor || value === lastKnownValue.current) return;
    editor.commands.setContent(value || '', { emitUpdate: false });
    lastKnownValue.current = value;
  }, [value, editor]);

  // Resolve every image id in the incoming content to a live URL. Images already mounted by
  // the time this resolves (the normal case - node views mount synchronously, this fetch
  // doesn't) just flip from their pending placeholder to the real picture once it lands.
  useEffect(() => {
    if (!editor) return;
    const ids = extractImageIds(value || '');
    if (ids.length === 0) return;
    let cancelled = false;
    documentsApi.resolveImageUrls(ids).then((urls) => {
      if (!cancelled) registerResolvedImageUrls(editor, urls);
    });
    return () => { cancelled = true; };
  }, [value, editor]);

  // Same batch-resolve as images above, for embedded PDFs - resolve-images is a generic
  // id-to-presigned-url endpoint, not image-specific, so it's reused as-is (see
  // resolve_image_urls's entityType check in the backend).
  useEffect(() => {
    if (!editor) return;
    const ids = extractPdfIds(value || '');
    if (ids.length === 0) return;
    let cancelled = false;
    documentsApi.resolveImageUrls(ids).then((urls) => {
      if (!cancelled) registerResolvedPdfUrls(editor, urls);
    });
    return () => { cancelled = true; };
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [readOnly, editor]);

  useEffect(() => () => editor?.destroy(), [editor]);

  if (!editor) return null;

  return (
    <div className={`np-policy-editor${readOnly ? ' is-readonly' : ''}`}>
      {!readOnly && (
        <>
          <div className="np-policy-editor-sticky-toolbar">
            <PolicyEditorInsertBar
              editor={editor}
              documentId={documentId}
              fileInputRef={fileInputRef}
              pdfInputRef={pdfInputRef}
            />
            <ToolbarDivider />
            <PolicyEditorBubbleContent editor={editor} />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handleImageFile(editor, file);
            }}
          />
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handlePdfFile(editor, file);
            }}
          />
        </>
      )}
      <div className="np-policy-editor-content-wrap">
        <EditorContent editor={editor} className="np-policy-editor-surface" style={{ minHeight }} />
      </div>
    </div>
  );
};

const ToolbarButton: React.FC<{
  icon: string;
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, title, active, disabled, onClick }) => (
  <button
    type="button"
    className={`np-icon-btn${active ? ' active' : ''}`}
    title={title}
    disabled={disabled}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
  >
    <i className={icon}></i>
  </button>
);

const ToolbarDivider: React.FC = () => <span className="np-policy-editor-divider" />;

/** The handful of actions that create something new rather than format something selected -
 * these don't fit the "appears near your selection" bubble menu since they often don't need a
 * selection at all. */
const PolicyEditorInsertBar: React.FC<{
  editor: Editor;
  documentId?: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  pdfInputRef: React.RefObject<HTMLInputElement | null>;
}> = ({ editor, documentId, fileInputRef, pdfInputRef }) => (
  <div className="np-policy-editor-insert-bar">
    <ToolbarButton
      icon="ri-image-add-line"
      title={documentId ? 'Insert image' : 'Save the document before adding images'}
      disabled={!documentId}
      onClick={() => fileInputRef.current?.click()}
    />
    <ToolbarButton
      icon="ri-file-pdf-2-line"
      title={documentId ? 'Insert PDF' : 'Save the document before adding PDFs'}
      disabled={!documentId}
      onClick={() => pdfInputRef.current?.click()}
    />
    <ToolbarButton
      icon="ri-table-2"
      title="Insert table"
      onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
    />
    <ToolbarButton
      icon="ri-separator"
      title="Insert divider"
      onClick={() => editor.chain().focus().setHorizontalRule().run()}
    />
    <ToolbarButton
      icon="ri-information-2-line"
      title="Insert callout"
      onClick={() => editor.chain().focus().setCallout('note').run()}
    />
    <ToolbarDivider />
    <ToolbarButton
      icon="ri-arrow-go-back-line"
      title="Undo"
      disabled={!editor.can().undo()}
      onClick={() => editor.chain().focus().undo().run()}
    />
    <ToolbarButton
      icon="ri-arrow-go-forward-line"
      title="Redo"
      disabled={!editor.can().redo()}
      onClick={() => editor.chain().focus().redo().run()}
    />
  </div>
);

const ALIGN_OPTIONS: { value: AlignValue; icon: string; title: string }[] = [
  { value: 'left', icon: 'ri-align-left', title: 'Align left' },
  { value: 'center', icon: 'ri-align-center', title: 'Align center' },
  { value: 'right', icon: 'ri-align-right', title: 'Align right' },
  { value: 'justify', icon: 'ri-align-justify', title: 'Justify' },
];

const CALLOUT_OPTIONS: { value: CalloutType; label: string }[] = [
  { value: 'note', label: 'Note' },
  { value: 'caution', label: 'Caution' },
  { value: 'warning', label: 'Warning' },
];

/** The contents of the single contextual bubble menu - what's rendered depends entirely on
 * what's currently selected, so an image never sees text-formatting controls and plain text
 * never sees table controls. */
const PolicyEditorBubbleContent: React.FC<{ editor: Editor }> = ({ editor }) => {
  if (editor.isActive('docImage')) {
    return (
      <div className="np-policy-editor-toolbar">
        {ALIGN_OPTIONS.filter((o) => o.value !== 'justify').map((o) => (
          <ToolbarButton
            key={o.value}
            icon={o.icon}
            title={o.title}
            active={editor.isActive('docImage', { align: o.value })}
            onClick={() => editor.chain().focus().updateAttributes('docImage', { align: o.value }).run()}
          />
        ))}
        <ToolbarDivider />
        <ToolbarButton
          icon="ri-delete-bin-line"
          title="Remove image"
          onClick={() => editor.chain().focus().deleteSelection().run()}
        />
      </div>
    );
  }

  if (editor.isActive('docPdf')) {
    return (
      <div className="np-policy-editor-toolbar">
        <ToolbarButton
          icon="ri-delete-bin-line"
          title="Remove PDF"
          onClick={() => editor.chain().focus().deleteSelection().run()}
        />
      </div>
    );
  }

  const activeHeadingLevel = HEADING_LEVELS.find((level) => editor.isActive('heading', { level }));
  const inTable = editor.isActive('table');
  const inCallout = editor.isActive('callout');

  return (
    <div className="np-policy-editor-toolbar">
      <select
        className="np-policy-editor-select"
        title="Text style"
        value={activeHeadingLevel ?? 'paragraph'}
        onChange={(e) => {
          const val = e.target.value;
          if (val === 'paragraph') editor.chain().focus().setParagraph().run();
          else editor.chain().focus().setHeading({ level: Number(val) as 1 | 2 | 3 | 4 | 5 }).run();
        }}
      >
        <option value="paragraph">Paragraph</option>
        {HEADING_LEVELS.map((level) => (
          <option key={level} value={level}>Heading {level}</option>
        ))}
      </select>
      <ToolbarDivider />
      <ToolbarButton icon="ri-bold" title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
      <ToolbarButton icon="ri-italic" title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <ToolbarButton icon="ri-underline" title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <ToolbarButton icon="ri-strikethrough" title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
      <ToolbarDivider />
      {ALIGN_OPTIONS.map((o) => (
        <ToolbarButton
          key={o.value}
          icon={o.icon}
          title={o.title}
          active={editor.isActive({ align: o.value })}
          onClick={() => editor.chain().focus().setTextAlign(o.value).run()}
        />
      ))}
      <ToolbarDivider />
      <ToolbarButton icon="ri-list-ordered" title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <ToolbarButton icon="ri-list-unordered" title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolbarButton
        icon="ri-indent-decrease"
        title="Decrease indent"
        disabled={!editor.can().liftListItem('listItem')}
        onClick={() => editor.chain().focus().liftListItem('listItem').run()}
      />
      <ToolbarButton
        icon="ri-indent-increase"
        title="Increase indent"
        disabled={!editor.can().sinkListItem('listItem')}
        onClick={() => editor.chain().focus().sinkListItem('listItem').run()}
      />
      <ToolbarDivider />
      <ToolbarButton
        icon="ri-link"
        title="Link"
        active={editor.isActive('link')}
        onClick={() => {
          const existing = editor.getAttributes('link').href as string | undefined;
          const url = window.prompt('Link URL', existing || 'https://');
          if (url === null) return;
          if (!url) { editor.chain().focus().unsetLink().run(); return; }
          editor.chain().focus().setLink({ href: url }).run();
        }}
      />
      <ToolbarButton icon="ri-double-quotes-l" title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
      <ToolbarButton icon="ri-code-box-line" title="Code block" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} />
      <ToolbarDivider />
      <ToolbarButton
        icon="ri-format-clear"
        title="Clear formatting"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      />
      {inTable && (
        <>
          <ToolbarDivider />
          <ToolbarButton icon="ri-insert-row-top" title="Add row above" onClick={() => editor.chain().focus().addRowBefore().run()} />
          <ToolbarButton icon="ri-insert-row-bottom" title="Add row below" onClick={() => editor.chain().focus().addRowAfter().run()} />
          <ToolbarButton icon="ri-delete-row" title="Delete row" onClick={() => editor.chain().focus().deleteRow().run()} />
          <ToolbarButton icon="ri-insert-column-left" title="Add column before" onClick={() => editor.chain().focus().addColumnBefore().run()} />
          <ToolbarButton icon="ri-insert-column-right" title="Add column after" onClick={() => editor.chain().focus().addColumnAfter().run()} />
          <ToolbarButton icon="ri-delete-column" title="Delete column" onClick={() => editor.chain().focus().deleteColumn().run()} />
          <ToolbarButton icon="ri-merge-cells-horizontal" title="Merge / split cells" onClick={() => editor.chain().focus().mergeOrSplit().run()} />
          <ToolbarButton icon="ri-layout-top-line" title="Toggle header row" onClick={() => editor.chain().focus().toggleHeaderRow().run()} />
          <ToolbarButton icon="ri-delete-bin-line" title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()} />
        </>
      )}
      {inCallout && (
        <>
          <ToolbarDivider />
          {CALLOUT_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`np-callout-type-btn np-callout-type-${o.value}${editor.isActive('callout', { calloutType: o.value }) ? ' active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                editor.chain().focus().updateAttributes('callout', { calloutType: o.value }).run();
              }}
            >
              {o.label}
            </button>
          ))}
          <ToolbarButton icon="ri-delete-bin-line" title="Remove callout" onClick={() => editor.chain().focus().unsetCallout().run()} />
        </>
      )}
    </div>
  );
};

export default PolicyEditor;
